/**
 * Global JavaScript
 * 全局 JavaScript - 主题初始化和全局功能
 * 
 * @description 主题的主要 JavaScript 文件，负责初始化和协调各个模块
 * @version 1.0.0
 */

const DealerTheme = (function() {
  'use strict';

  const config = {
    breakpoints: {
      mobile: 768,
      tablet: 1024,
      desktop: 1280
    },
    selectors: {
      header: '[data-header]',
      mobileMenu: '[data-mobile-menu]',
      mobileMenuToggle: '[data-mobile-menu-toggle]',
      searchToggle: '[data-search-toggle]',
      cartIcon: '[data-cart-icon]',
      lazyImages: '[data-lazy-load]'
    }
  };

  let state = {
    isMobileMenuOpen: false,
    isSearchOpen: false,
    currentBreakpoint: 'desktop'
  };

  /**
   * 初始化主题
   */
  function init() {
    console.log('🚀 Dealer Theme Initializing...');

    // 初始化各个模块
    initHeader();
    initMobileMenu();
    initAccessibility();
    initLazyLoading();
    initScrollBehavior();
    initPriceDisplay();
    
    // 监听窗口大小变化
    handleResize();
    window.addEventListener('resize', DealerThemeUtils.debounce(handleResize, 250));

    // 主题初始化完成事件
    document.dispatchEvent(new CustomEvent('theme:loaded'));
    console.log('✅ Dealer Theme Loaded');
  }

  /**
   * 初始化头部
   */
  function initHeader() {
    const header = document.querySelector(config.selectors.header);
    if (!header) return;

    // 滚动时添加阴影效果
    let lastScroll = 0;
    window.addEventListener('scroll', DealerThemeUtils.throttle(() => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      // 滚动方向检测
      if (currentScroll > lastScroll && currentScroll > 200) {
        header.classList.add('header--hidden');
      } else {
        header.classList.remove('header--hidden');
      }

      lastScroll = currentScroll;
    }, 100));
  }

  /**
   * 初始化移动菜单
   */
  function initMobileMenu() {
    const menuToggle = document.querySelector(config.selectors.mobileMenuToggle);
    const mobileMenu = document.querySelector(config.selectors.mobileMenu);

    if (!menuToggle || !mobileMenu) return;

    menuToggle.addEventListener('click', () => {
      toggleMobileMenu();
    });

    // 点击遮罩层关闭菜单
    const overlay = document.createElement('div');
    overlay.className = 'mobile-menu-overlay';
    overlay.setAttribute('data-mobile-menu-overlay', '');
    document.body.appendChild(overlay);

    overlay.addEventListener('click', () => {
      if (state.isMobileMenuOpen) {
        toggleMobileMenu();
      }
    });

    // ESC 键关闭菜单
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isMobileMenuOpen) {
        toggleMobileMenu();
      }
    });
  }

  /**
   * 切换移动菜单
   */
  function toggleMobileMenu() {
    const mobileMenu = document.querySelector(config.selectors.mobileMenu);
    const menuToggle = document.querySelector(config.selectors.mobileMenuToggle);
    const overlay = document.querySelector('[data-mobile-menu-overlay]');

    if (!mobileMenu || !menuToggle) return;

    state.isMobileMenuOpen = !state.isMobileMenuOpen;

    if (state.isMobileMenuOpen) {
      mobileMenu.classList.add('is-open');
      menuToggle.classList.add('is-active');
      menuToggle.setAttribute('aria-expanded', 'true');
      overlay.classList.add('is-visible');
      DealerThemeUtils.toggleBodyScroll(true);

      // 聚焦到第一个可聚焦元素
      const firstFocusable = mobileMenu.querySelector('a, button');
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    } else {
      mobileMenu.classList.remove('is-open');
      menuToggle.classList.remove('is-active');
      menuToggle.setAttribute('aria-expanded', 'false');
      overlay.classList.remove('is-visible');
      DealerThemeUtils.toggleBodyScroll(false);
      menuToggle.focus();
    }
  }

  /**
   * 初始化价格显示
   */
  function initPriceDisplay() {
    // 检查用户权限
    const isDealer = DealerThemeUtils.isDealerUser();
    const isLoggedIn = DealerThemeUtils.isLoggedIn();

    // 将用户状态添加到 body，方便 CSS 控制样式
    if (isLoggedIn) {
      document.body.classList.add('customer-logged-in');
    }

    if (isDealer) {
      document.body.classList.add('customer-is-dealer');
    }

    // 触发价格显示更新事件
    document.dispatchEvent(new CustomEvent('theme:priceDisplayUpdate', {
      detail: { isDealer, isLoggedIn }
    }));
  }

  /**
   * 初始化可访问性功能
   */
  function initAccessibility() {
    // 为所有图片添加 alt 属性（如果缺失）
    document.querySelectorAll('img:not([alt])').forEach(img => {
      img.setAttribute('alt', '');
    });

    // 键盘导航焦点可见性
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-navigation');
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-navigation');
    });
  }

  /**
   * 初始化懒加载
   */
  function initLazyLoading() {
    DealerThemeUtils.lazyLoadImages(config.selectors.lazyImages);
  }

  /**
   * 初始化滚动行为
   */
  function initScrollBehavior() {
    // 平滑滚动到锚点
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        
        // 忽略空锚点
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          const headerHeight = document.querySelector(config.selectors.header)?.offsetHeight || 0;
          DealerThemeUtils.scrollToElement(target, headerHeight + 20);
        }
      });
    });

    // 返回顶部按钮
    const backToTop = document.querySelector('[data-back-to-top]');
    if (backToTop) {
      window.addEventListener('scroll', DealerThemeUtils.throttle(() => {
        if (window.pageYOffset > 500) {
          backToTop.classList.add('is-visible');
        } else {
          backToTop.classList.remove('is-visible');
        }
      }, 100));

      backToTop.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  /**
   * 处理窗口大小变化
   */
  function handleResize() {
    const newBreakpoint = DealerThemeUtils.getCurrentBreakpoint();
    
    if (newBreakpoint !== state.currentBreakpoint) {
      state.currentBreakpoint = newBreakpoint;
      
      // 触发断点变化事件
      document.dispatchEvent(new CustomEvent('theme:breakpointChange', {
        detail: { breakpoint: newBreakpoint }
      }));

      // 移动端切换到桌面端时关闭移动菜单
      if (newBreakpoint !== 'mobile' && state.isMobileMenuOpen) {
        toggleMobileMenu();
      }
    }

    // 更新 CSS 自定义属性（用于 JS 和 CSS 通信）
    document.documentElement.style.setProperty('--window-height', `${window.innerHeight}px`);
  }

  /**
   * 获取当前状态
   * @returns {Object} 当前状态对象
   */
  function getState() {
    return { ...state };
  }

  /**
   * 获取配置
   * @returns {Object} 配置对象
   */
  function getConfig() {
    return { ...config };
  }

  // 公开 API
  return {
    init,
    getState,
    getConfig,
    toggleMobileMenu
  };
})();

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', DealerTheme.init);
} else {
  DealerTheme.init();
}

// 暴露到全局
window.DealerTheme = DealerTheme;

